import { Events } from "@Context/common/general/context";
import { NavigationEvent } from "@Globals/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 *
 * @returns a ref to be used on a scrollable element to save and restore the scroll position based on the current pathname
 */
const useScrollRestoration = () => {
  const { pathname } = useLocation();
  const [height, setHeight] = useState(0);
  const containerRef = useRef<any>(null);

  const getScrollHistory = () => JSON.parse(localStorage.getItem("scrollHistory") || "{}");

  const setScrollHistory = (scrollHistory: any) => localStorage.setItem("scrollHistory", JSON.stringify(scrollHistory));

  // Observe the element for changes in size
  const scrollfulRef = useCallback((node: any) => {
    if (!node) return;
    const resizeObserver = new MutationObserver(() => {
      containerRef.current = node;
      setHeight(node.getBoundingClientRect().height);
    });
    resizeObserver.observe(node, { attributes: true, childList: true, subtree: true });
  }, []);

  const saveScrollPosition = (event: NavigationEvent) => {
    if (containerRef.current && event.path.includes(pathname)) {
      const scrollHistory = getScrollHistory();
      scrollHistory[pathname] = containerRef.current.scrollTop;
      setScrollHistory(scrollHistory);
    }
  };

  // Save scroll y position on navigation
  useEffect(() => {
    Events.app.navigate.uniq(saveScrollPosition);
    Events.app.navigateExclusive.uniq(saveScrollPosition);
    return () => {
      Events.app.navigate.remove(saveScrollPosition);
      Events.app.navigateExclusive.remove(saveScrollPosition);
    };
  }, []);

  // Scroll to saved y position on mount
  useEffect(() => {
    if (containerRef.current && height > 0) {
      const scrollHistory = getScrollHistory();
      if (scrollHistory[pathname]) {
        containerRef.current.scrollTo(0, scrollHistory[pathname]);
        delete scrollHistory[pathname];
        setScrollHistory(scrollHistory);
      }
    }
  }, [height, pathname]);

  return {
    scrollfulRef,
  };
};

export default useScrollRestoration;
